import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Plus, ClipboardCheck, SlidersHorizontal } from "lucide-react";
import { DataTable, type Column, Pill, Button, EmptyState, ToolbarSelect } from "@/design/components";
import { formatCardDate, formatRelativeShort } from "@/design/format";
import { useProjects, useSiteVisits, useActorId } from "@/domain/hooks";
import { useStartSiteVisit } from "@/domain/useMutations";
import type { SiteVisit } from "@/domain/types";
import { toast } from "sonner";

const STATUS_META: Record<SiteVisit["status"], { label: string; variant: "neutral" | "info" | "success" }> = {
  planned: { label: "Planned", variant: "neutral" },
  in_progress: { label: "In progress", variant: "info" },
  complete: { label: "Complete", variant: "success" },
};

export function SiteVisitsList() {
  const projects = useProjects();
  const visits = useSiteVisits();
  const actorId = useActorId();
  const { start: startVisit, isPending: starting } = useStartSiteVisit();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");

  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? "–";

  async function start() {
    const newId = await startVisit({ projectId, visitorId: actorId });
    if (newId) navigate(`/stratus/site-visits/${newId}`);
    toast.success(`Site visit started at ${projectName(projectId)}`);
  }

  const columns: Column<SiteVisit>[] = [
    {
      key: "project",
      header: "Project",
      cell: (v) => (
        <div>
          <p className="font-medium text-ink">{projectName(v.projectId)}</p>
        </div>
      ),
    },
    {
      key: "when",
      header: "When",
      cell: (v) =>
        v.status === "complete" && v.completedAt ? (
          <span className="data text-sm text-mute">{formatCardDate(v.completedAt)}</span>
        ) : v.status === "in_progress" && v.startedAt ? (
          <span className="text-sm text-mute">
            Started <span className="data text-ink">{formatRelativeShort(v.startedAt)}</span> ago
          </span>
        ) : v.plannedFor ? (
          <span className="text-sm text-mute">
            Planned <span className="data text-ink">{formatCardDate(v.plannedFor)}</span>
          </span>
        ) : (
          <span className="text-sm text-faint">Planned</span>
        ),
    },
    {
      key: "findings",
      header: "Discrepancies",
      // A discrepancy is any checklist item judged "not as claimed" or "partial" —
      // the same definition the visit report uses when it raises findings.
      cell: (v) => {
        const n = v.checklist.filter((c) => c.judgement === "not_as_claimed" || c.judgement === "partial").length;
        return n ? <Pill variant="warn">{n} found</Pill> : <span className="text-xs text-faint">–</span>;
      },
    },
    { key: "status", header: "Status", cell: (v) => <Pill variant={STATUS_META[v.status].variant}>{STATUS_META[v.status].label}</Pill> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (v) => (
        <Button size="sm" variant="secondary" onClick={() => navigate(`/stratus/site-visits/${v.id}`)}>
          {v.status === "planned" ? "Open" : v.status === "in_progress" ? "Continue" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-line bg-card p-4">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-ink"><ClipboardCheck className="h-4 w-4 text-brand" /> Start a site visit</p>
          <p className="mt-0.5 text-xs text-mute">The checklist is built from the questions oversight has set. Captures are analysed against the ledger as you go.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Link
            to="/stratus/portfolio/site-visits/settings"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-card px-3 text-sm font-medium text-ink hover:bg-subtle"
          >
            <SlidersHorizontal className="h-4 w-4" /> Manage questions
          </Link>
          <ToolbarSelect
            value={projectId}
            onChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            className="max-w-[14rem]"
          />
          <Button disabled={starting} onClick={start}><Plus className="h-4 w-4" /> Start visit</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={visits}
        rowKey={(v) => v.id}
        onRowClick={(v) => navigate(`/stratus/site-visits/${v.id}`)}
        empty={<EmptyState icon={<MapPin className="h-5 w-5" />} title="No site visits yet" body="Start a visit above to capture structured observations on site and have them analysed against the ledger." />}
      />
    </div>
  );
}
